import Api from 'util/Api';
import API from 'constants/api';
import { message, notification } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Shipment } from 'interfaces/journey';
import { JourneyResponse } from './journeys';

export interface ArrivedPackage {
  externalCode: string;
  status: number;
  isBundle?: boolean;
  shippingScheduleDestination: {
    zone: {
      name: string | null | undefined;
    };
  };
}

export interface ArrivePacketResponse {
  data: {
    arrived?: ArrivedPackage;
    foundPackages?: FoundPackage[];
    message?: string;
  };
  message?: string;
}

export interface DeliverPacketResponse {
  data: {
    foundPackages?: FoundPackage[];
  };
  message?: string;
  status?: number;
}

export interface FoundPackage {
  id: number;
  externalCode: string;
  marketplace?: {
    name?: string | null | undefined;
  };
  packageType: number;
  packageSize: number;
  status: number;
  deliveryTerm: number;
  isBundle: boolean;
  shippingScheduleDestination: {
    shippingAddress: {
      addressDetail: string;
    };
    zone: {
      name: string | null | undefined;
    };
  };
}

/**
 * @description
 * Arriba un paquete por el codigo externo
 */
export const arrivePacketByExternalCode = async (
  externalCode: string | null | undefined,
) => {
  try {
    const searchParams = new URLSearchParams({
      searchArrived: externalCode as string,
    });
    const response = await Api.apiAxios.put(
      `${API.packages.arrivePacket}?${searchParams.toString()}`,
    );

    if (!Api.isSuccessResponse(response)) {
      throw new Error(response.data.message);
    }

    const arriveResponse: ArrivePacketResponse = response.data;

    return arriveResponse;
  } catch (error: any) {
    if (error.isAxiosError) {
      throw error;
    }
    throw new Error(error.message);
  }
};

/**
 * @description
 * Arriba un paquete por el id exacto (id de base)
 */
export const arrivePacketByID = async (id: number) => {
  try {
    const response = await Api.apiAxios.put(
      `${API.packages.arrivePacketByID(id)}`,
    );
    if (!Api.isSuccessResponse(response)) {
      throw new Error(response.data.message);
    }
    return response;
  } catch (error: any) {
    if (error.isAxiosError) {
      throw error;
    }
    throw new Error(error.message);
  }
};

/**
 * @description
 * Entrega un paquete pickup por el codigo externo
 */
export const deliverPickupPacketByExternalCode = async (
  externalCode: string | null | undefined,
) => {
  try {
    const request = {
      code: externalCode as string,
    };
    const response = await Api.apiAxios.post(
      `${API.packages.deliverPickup}`,
      request,
    );

    if (!Api.isSuccessResponse(response)) {
      throw new Error(response.data.message);
    }

    const deliveryResponse: DeliverPacketResponse = response;

    return deliveryResponse;
  } catch (error: any) {
    if (error.isAxiosError) {
      throw error;
    }
    throw new Error(error.message);
  }
};

/**
 * @description
 * Entrega un paquete pickup por el id exacto (id de base)
 */
export const deliverPickupPacketByID = async (id: number) => {
  try {
    const response = await Api.apiAxios.post(
      `${API.packages.deliverPickupByID(id)}`,
    );
    if (!Api.isSuccessResponse(response)) {
      throw new Error(response.data.message);
    }
    return response.data;
  } catch (error: any) {
    if (error.isAxiosError) {
      throw error;
    }
    throw new Error(error.message);
  }
};

const getByExternalCode = async (
  externalCode?: string,
): Promise<Shipment[] | undefined> => {
  if (!externalCode) return;
  try {
    const searchParams = new URLSearchParams({
      externalCode,
    });
    const url = `${API.packages.getByExternalCodeFromArrivedOrDispatch}?${searchParams.toString()}`;
    const response = await Api.apiAxios.get(url);

    if (!Api.isSuccessResponse(response)) {
      throw new Error(response.data.message);
    }

    return response.data.content;
  } catch (error: any) {
    if (error.isAxiosError) {
      throw error;
    }
    throw new Error(error.message);
  }
};

export const useGetByExternalCode = (
  externalCode?: string,
  journeyId?: string,
) => {
  const queryClient = useQueryClient();
  return useQuery<Shipment[] | undefined, Error>({
    queryKey: ['packagesByExternal', externalCode],
    queryFn: async () => {
      const data = await getByExternalCode(externalCode);
      const draftJourney = queryClient.getQueryData<JourneyResponse>([
        'journey',
        journeyId,
      ]);

      if (draftJourney)
        if (!data || !journeyId) {
          queryClient.setQueryData(['journey', journeyId], {
            ...draftJourney,
            inputResponse: 500,
            inputtedExternal: externalCode,
          });
          message.error('Ocurrió un error.');
        } else if (data.length === 0) {
          queryClient.setQueryData(['journey', journeyId], {
            ...draftJourney,
            inputResponse: 404,
            inputtedExternal: externalCode,
          });
          message.info('No se encontró el paquete.');
        }

      return data;
    },
    retry: 1,
    enabled: !!externalCode,
  });
};

export const getPackage = async (id: string): Promise<any> => {
  const response = await Api.apiAxios.get(`${API.packages.getById}${id}`);
  return response.data.data;
};

type FindShipmentsByExternalCodeParams = {
  externalCode: string;
  page: number;
  size: number;
};

const findPackagesByExternalCode = async ({
  size,
  externalCode,
  page,
}: FindShipmentsByExternalCodeParams) => {
  const searchParams = new URLSearchParams({
    externalCode,
    page: page.toString(),
    size: size.toString(),
  });
  const packages = await Api.apiAxios.get<{ content: Shipment[] }>(
    `${API.packages.getByExternalCode}?${searchParams.toString()}`,
  );
  return packages.data;
};

export const useFindPackagesByExternalCode = (
  params: FindShipmentsByExternalCodeParams,
) => {
  return useQuery({
    queryKey: ['findPackagesByExternalCode', params],
    queryFn: () => findPackagesByExternalCode(params),
    refetchOnWindowFocus: false,
  });
};
